import React, { useMemo } from "react";
import { parseMarkdown, sanitizeHtml } from "../lib/markdown";

/**
 * Element styles, written out because the project has no typography plugin. Each tone is
 * the arbitrary-variant class string for its plane: `light` is the super-admin console
 * (slate on white), `dark` the public blog (white on sol-midnight).
 */
const BASE = [
  "[&_h1]:text-3xl [&_h1]:font-semibold [&_h1]:mt-8 [&_h1]:mb-4",
  "[&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3",
  "[&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2",
  "[&_p]:my-4 [&_p]:leading-relaxed",
  "[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-4 [&_li]:my-1",
  "[&_a]:underline [&_a]:underline-offset-2",
  "[&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4",
  "[&_code]:rounded [&_code]:px-1 [&_code]:font-mono [&_code]:text-[0.9em]",
  "[&_pre]:rounded-lg [&_pre]:p-4 [&_pre]:overflow-x-auto [&_pre]:my-4 [&_pre_code]:bg-transparent [&_pre_code]:p-0",
  "[&_img]:rounded-xl [&_img]:max-w-full [&_img]:my-6",
  "[&_hr]:my-8 [&_table]:my-4 [&_th]:px-3 [&_th]:py-1 [&_td]:px-3 [&_td]:py-1 [&_th]:text-left",
].join(" ");

const TONES = {
  light: `${BASE} text-slate-700 [&_a]:text-amber-700 [&_blockquote]:border-slate-300 [&_code]:bg-slate-100 [&_pre]:bg-slate-100 [&_td]:border [&_th]:border [&_td]:border-slate-200 [&_th]:border-slate-200`,
  dark: `${BASE} text-white/80 [&_a]:text-sol-gold [&_blockquote]:border-sol-gold/40 [&_code]:bg-white/10 [&_pre]:bg-white/5 [&_td]:border [&_th]:border [&_td]:border-white/10 [&_th]:border-white/10`,
};

/**
 * A Markdown source rendered as styled, sanitized HTML. The sanitize call sits in the
 * `dangerouslySetInnerHTML` prop itself, so the pre-commit guard can see it.
 *
 * @param {string} source Markdown text.
 * @param {"light" | "dark"} [tone]
 * @param {string} [testId]
 */
export default function MarkdownBody({ source, tone = "light", testId }) {
  const rawHtml = useMemo(() => parseMarkdown(source), [source]);
  return (
    <div
      className={TONES[tone] || TONES.light}
      data-testid={testId}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(rawHtml) }}
    />
  );
}
